import ReactDataSheet from 'react-datasheet';
import { CSSProperties, useEffect, useRef } from 'react';
import { useDataSheetPager, useSharedDataSheet } from './context';
import CellRendererProps = ReactDataSheet.CellRendererProps;
import { GridElement, Value } from './types';


const shouldRed = (grid: GridElement) => {
  if (typeof grid.value === 'number') {
    return grid.value < 0;
  }
  return false;
};

const checked = (grid: GridElement) => {
  if (typeof grid.value === 'boolean') {
    return grid.value;
  }
  return false;
};

function createProperty(props: CellRendererProps<GridElement, Value>): CSSProperties {
  const res:CSSProperties = {};
  if (shouldRed(props.cell)) {
    res.color = 'red';
  }
  if (props.cell.width) {
    res.width = props.cell.width + 'px';
    res.minWidth = props.cell.width + 'px';
    res.maxWidth = props.cell.width + 'px';
  } else {
    res.minWidth = '60px';
  }
  return res;
}

// eslint-disable-next-line max-len
export const CellRenderer: ReactDataSheet.CellRenderer<GridElement, Value> = (props: CellRendererProps<GridElement, Value>) => {
  const backgroundStyle: CSSProperties = createProperty(props);
  const ref = useRef<HTMLTableCellElement>();
  const sharedData = useSharedDataSheet();
  const pager = useDataSheetPager();

  useEffect(() => {
    if (props.cell.freezeX) {
      if (ref.current && sharedData.left) {
        const zIndex = props.cell.freezeY ? 22 : 21;
        const scrollLeft = sharedData.parent.current?.scrollLeft ?? 0;
        const left = scrollLeft + ref.current.getBoundingClientRect().left - (sharedData.left ?? 0);
        ref.current.style.position = 'sticky';
        ref.current.style.left = left + 'px';
        ref.current.style.zIndex = '' + zIndex;
      }
    }
    return () => {
      if (ref.current) {
        ref.current.style.left = '';
      }
    };
  }, [props.cell.width, props.cell.freezeX, props.cell.freezeY, sharedData.left, pager.page]);

  useEffect(() => {
    if (props.cell.freezeY) {
      if (ref.current && sharedData.top) {
        const zIndex = props.cell.freezeX ? 22 : 21;
        ref.current.style.position = 'sticky';
        ref.current.style.top = (props.cell.height * props.row) + 'px';
        ref.current.style.zIndex = '' + zIndex;
      }
    }
    return () => {
      if (ref.current) {
        ref.current.style.top = '';
      }
    };
  }, [props.cell.freezeY, props.cell.freezeX, sharedData.top, pager.page]);

  return (
        <td
            key={props.row + '-' + props.col}
            ref={ref}
            style={backgroundStyle}
            onMouseDown={props.onMouseDown}
            onMouseOver={props.onMouseOver}
            onDoubleClick={props.onDoubleClick}
            className={
                'cell'
                + (props.cell.freezeX || props.cell.freezeY ? ' freeze' : '')
                + (props.cell.freezeX ? ' freezeX' : '')
                + (props.cell.freezeY ? ' freezeY' : '')
                + (props.cell.readOnly ? ' read-only' : '')
                + (props.selected ? ' ' + 'selected' : '')
                + (props.editing ? ' ' + 'editing' : '')
                + (props.cell.isEdit ? ' edited' : '')
                + (props.cell.invalid ? ' invalid' : '')
            }
            colSpan={props.cell.colSpan}
            rowSpan={props.cell.rowSpan}
        >
            <pre>{props.children}</pre>
        </td>
  );
};
